import React, { useEffect } from 'react'
import { ProjectNotesDialog } from './ProjectNotesDialog'
import { useProjectNotes } from './useProjectNotes'
import useBrowserTheme from '../../hooks/useBrowserTheme'
import { onEvent, exposeGlobalAddToNotes } from './legacyBridge'
import { usePanelStore } from '../../stores/usePanelStore'
import {
    useOnboardingStore,
    GUIDE_SIGNALS,
} from '../../stores/useOnboardingStore'

const isProjectNotesOpenSelector = state => state.isProjectNotesOpen
const setProjectNotesOpenSelector = state => state.setProjectNotesOpen
const toggleProjectNotesPanelSelector = state => state.toggleProjectNotesPanel

export default function ProjectNotes() {
    // Open-state lives in usePanelStore so the PanelToggles' button can
    // read/write it. Event listeners below also mutate via the store.
    const open = usePanelStore(isProjectNotesOpenSelector)
    const setOpen = usePanelStore(setProjectNotesOpenSelector)
    const togglePanel = usePanelStore(toggleProjectNotesPanelSelector)
    const theme = useBrowserTheme(state => state.theme)


    const {
        notesHtml,
        notesPlain,
        saveStatus,
        isGeneratingAnalytics,
        scrollToBottomTrigger,
        handleEditorChange,
        handleAutoGenerateAnalytics,
        handleOpenAI,
        handleCopy,
        handleDownload,
        handleGenerateGraph,
        handleAddToNotes,
    } = useProjectNotes()

    // Expose addCommentToProjectNotes globally for AI chat CopyToProjectNotesButton
    useEffect(() => {
        exposeGlobalAddToNotes(text => {
            handleAddToNotes(text)
            setOpen(true)
            // Onboarding-guide observation point: every AI-widget save to
            // Project Notes flows through this global — the panel opening by
            // itself is the user's confirmation, the signal is the guide's.
            const onboardingState = useOnboardingStore.getState()
            if (onboardingState.status === 'active') {
                onboardingState.recordSignal(GUIDE_SIGNALS.SAVED_TO_NOTES)
            }
        })
    }, [handleAddToNotes])

    // Sync tw-dark class for Tailwind dark: variants.
    // Runs on mount, on theme store change, and on legacy themeChange event.
    useEffect(() => {
        const syncDark = () => {
            const isDark =
                theme === 'dark' ||
                document.documentElement.hasAttribute('theme')
            document.documentElement.classList.toggle('tw-dark', isDark)
        }
        syncDark()
        return onEvent('themeChange', syncDark)
    }, [theme])

    // Listen to legacy event emitter for WorkflowStepper integration.
    // Handlers call store actions directly — they're stable references, so
    // the effect can keep an empty deps array without staleness.
    useEffect(() => {
        const showPanel = () => setOpen(true)
        const hidePanel = () => setOpen(false)

        const unsubs = [
            onEvent('showProjectNotesPanel', showPanel),
            onEvent('hideProjectNotesPanel', hidePanel),
            onEvent('showProjectNotesPanelAndShare', showPanel),
            onEvent('hideProjectNotesPanelAndShare', hidePanel),
            onEvent('toggleProjectNotesPanel', togglePanel),
        ]

        return () => unsubs.forEach(fn => fn())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Hide old EJS panel. The legacy `#info-link` trigger is hidden via CSS
    // in projectNotes.css (tooltipster clears inline styles).
    useEffect(() => {
        const oldPanel = document.getElementById('graphInfoData')
        if (oldPanel) oldPanel.style.display = 'none'
    }, [])

    return (
        <ProjectNotesDialog
            open={open}
            onOpenChange={setOpen}
            notesHtml={notesHtml}
            notesPlain={notesPlain}
            saveStatus={saveStatus}
            isGeneratingAnalytics={isGeneratingAnalytics}
            scrollToBottomTrigger={scrollToBottomTrigger}
            onEditorChange={handleEditorChange}
            onAutoGenerateAnalytics={handleAutoGenerateAnalytics}
            onOpenAI={handleOpenAI}
            onCopy={handleCopy}
            onDownload={handleDownload}
            onGenerateGraph={handleGenerateGraph}
        />
    )
}
